import React from 'react';
import { AxiosError } from 'axios';
import {
    removeAdminToken,
    removeOktaTokens,
} from '../shared/storageService/cookieService';
import OktaAuth from '@okta/okta-auth-js';
import { TFunction } from 'react-i18next';
import { toast } from 'react-toastify';
import { Warning32 } from '@carbon/icons-react';
import { i18n } from 'i18next';
import { NavigateFunction } from 'react-router-dom';

// 1. Define the shape of the API error response
interface ApiErrorResponse {
    message?: string;
    errorId?: string;
    title?: string;
    detail?: string;
    [key: string]: any;
}

const findTranslationKey = (
    key: string,
    i18nObject: i18n,
    t: TFunction<string, unknown>
) => {
    const namespaces = i18nObject.options.ns as string[];

    let translationNameSpace;
    if (namespaces && namespaces?.length > 0) {
        const foundKey = namespaces.some((namespace) => {
            const translation = t(`${namespace}:${key}`);

            if (translation !== key) {
                translationNameSpace = namespace;
                return true;
            }
            return false;
        });
        if (foundKey) {
            return t(`${translationNameSpace}:${key}`);
        }
    }
};

const copyMessageToClipBoard = (text: string) => {
    navigator.clipboard.writeText(text);
};

const copyToClipboard = (text: string, t: TFunction<string, unknown>) => {
    copyMessageToClipBoard(text);
    const copiedMessage = t('form-content:ErrorMessageCopiedToClipboard');
    toast.success(copiedMessage, {
        onClose: () => copyMessageToClipBoard(copiedMessage),
    });
};

export const errorHandler = (
    error: AxiosError,
    oktaAuth: OktaAuth,
    restoreOriginalUri: (
        _oktaAuth: OktaAuth,
        originalUri: string
    ) => Promise<void>,
    t: TFunction<string, unknown>,
    i18nObject: i18n,
    navigate: NavigateFunction,
    copyErrorMessageMef2099?: boolean
) => {
    // 2. Extract URL and Method safely (Fixes "config is possibly undefined")
    const requestUrl = error.config?.url;
    const requestMethod = error.config?.method;

    const oktaRoutesCondition =
        requestUrl?.includes('testapp/names/') ||
        requestUrl?.includes('testapp/names-dynamic/') ||
        requestUrl?.includes('testapp/selector/') ||
        requestUrl?.includes('form/selector/') ||
        requestUrl?.includes('rep/form/') ||
        requestUrl?.includes('configurator/');

    const adminRoutesCondition =
        (requestUrl?.includes('rep/form/') &&
            requestMethod === 'delete') ||
        (requestUrl?.includes('rep/form/selector/') &&
            requestMethod === 'get') ||
        requestUrl?.includes('rep/form/url/') ||
        requestUrl?.includes('rep/form/create/') ||
        requestUrl?.includes('rep/form/copy/');

    // 3. Check header safely (Fixes dot-notation and undefined issues)
    // eslint-disable-next-line dot-notation
    const skipToast = error.config?.headers ? error.config.headers['SkipToast'] : false;

    if (error.response?.status === 401 || error.response?.status === 403) {
        if (oktaRoutesCondition) {
            removeOktaTokens();
            navigate('/unauthorized');
        }
        if (adminRoutesCondition) {
            removeAdminToken();
        }
    } else if (!skipToast) {
        const { response } = error;

        // 4. Cast response data to the Interface created above (Fixes "unknown" type errors)
        const responseData = response?.data as ApiErrorResponse | string | undefined;

        let errorMessage = '';
        let errorMessageWithoutErrorId;

        if (response?.status && response?.status > 500) {
            errorMessage = t('toast-content:ToastInternalServerError');
        } else if (typeof responseData === 'object' && responseData?.message) {
            const translation = findTranslationKey(
                responseData.message,
                i18nObject,
                t
            );

            errorMessageWithoutErrorId = translation ? t(translation) : '';
            errorMessage =
                `${translation ? t(translation) : ''} \n` +
                `${responseData.errorId ? responseData.errorId : ''}`;
        } else if (typeof responseData === 'string' && responseData !== '') {
            errorMessage = t(responseData);
        } else if (typeof responseData === 'object' && responseData?.title) {
            errorMessage = t(responseData.title);
        } else if (typeof responseData === 'object' && responseData?.detail) {
            errorMessage = t(responseData.detail);
        } else {
            errorMessage = t('toast-content:ToastUnexpectedError');
        }

        const toastId = requestUrl ?? 'unknown-error';

        if (toast.isActive(toastId)) {
            toast.update(toastId, {
                render: (
                    <p className="line-clamp-3">{errorMessage.toString()}</p>
                ),
                type: 'error',
                isLoading: false,
                delay: 1000,
                autoClose: 10 * 1000,
                icon: <Warning32 />,
                closeOnClick: true,
                onClick: () => copyToClipboard(errorMessage, t),
                closeButton: copyErrorMessageMef2099,
            });
        } else {
            toast.error(
                <p className="line-clamp-3">{errorMessage.toString()}</p>,
                {
                    toastId: errorMessageWithoutErrorId ?? errorMessage,
                    autoClose: 10 * 1000,
                    onClick: () => copyToClipboard(errorMessage, t),
                    closeButton: copyErrorMessageMef2099,
                }
            );
        }
    }
    return error;
};
